import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _typeof from "@babel/runtime/helpers/typeof";
var _excluded = ["children", "spacing", "withDividers", "breakpoints", "as"],
  _excluded2 = ["children", "as", "spacing", "withDividers", "breakpoints"],
  _excluded3 = ["children", "hiddenAbove", "hiddenBelow", "allowUnknownProps"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.map.js";
import "core-js/modules/es.number.constructor.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React, { createContext, useContext, forwardRef } from 'react';
import styled from 'styled-components';
import { getSpacing, createStyleFunction, getPropFilter } from 'utils/ui';
import Box from 'components/Box';
import Divider from 'components/Divider';
import Hidden from 'components/Hidden';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var spacingCompensationProp = createStyleFunction([{
  styleProp: 'compensateSpacing',
  properties: ['marginBottom'],
  getValue: function getValue(value, theme) {
    return "-".concat(getSpacing(value, theme));
  }
}]);
var dividerCompensationProp = createStyleFunction([{
  styleProp: 'compensateSpacing',
  properties: ['marginBottom'],
  getValue: function getValue(value, theme) {
    var spacing = getSpacing(value, theme);
    if (Number(spacing) === 0) {
      return '-1px';
    } else {
      return "calc(-".concat(spacing, " * 2 - 1px)");
    }
  }
}]);
/**
 * Using a pseudo element to compensate for the spacing
 * between items (instead of negative margin) to allow
 * for the wrapper to seamlessly accept any `Box` props.
 * E.g., if a negative margin-top was used instead, it would
 * clash with the `mt` prop.
 */

var Wrapper = styled(Box).withConfig({
  shouldForwardProp: getPropFilter(['compensateSpacing', 'withDividers'])
}).withConfig({
  displayName: "Stack__Wrapper",
  componentId: "sc-f4n4bf-0"
})(["", " &::before{content:'';display:block;height:0;", "}"], function (p) {
  return p.withDividers && "\n\t\toverflow: hidden;\n\t";
}, function (p) {
  return p.withDividers ? dividerCompensationProp : spacingCompensationProp;
});
function getRoles(as) {
  switch (as) {
    case 'list':
      return ['ul', 'li'];
    case 'ordered-list':
      return ['ol', 'li'];
    default:
      return ['div', 'div'];
  }
}
function getHiddenChildProps(child) {
  if (_typeof(child) === 'object' && child.type === Hidden) {
    return child.props;
  } else return null;
}
function Stack(_ref) {
  var children = _ref.children,
    spacing = _ref.spacing,
    withDividers = _ref.withDividers,
    breakpoints = _ref.breakpoints,
    _ref$as = _ref.as,
    as = _ref$as === void 0 ? 'default' : _ref$as,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var _getRoles = getRoles(as),
    _getRoles2 = _slicedToArray(_getRoles, 2),
    wrapperAs = _getRoles2[0],
    itemAs = _getRoles2[1];
  return /*#__PURE__*/_jsx(Wrapper, _objectSpread(_objectSpread({}, otherProps), {}, {
    withDividers: withDividers,
    compensateSpacing: spacing,
    as: wrapperAs,
    breakpoints: breakpoints,
    children: React.Children.map(children, function (child) {
      if (!child) return null;
      var hiddenChildProps = getHiddenChildProps(child);
      if (hiddenChildProps && (hiddenChildProps.above || hiddenChildProps.below)) {
        if (hiddenChildProps !== null && hiddenChildProps !== void 0 && hiddenChildProps.as) {
          // eslint-disable-next-line no-console
          console.warn('The prop `as` is not valid on a Hidden component. The element used will be determined by the Stack.');
        }
        return /*#__PURE__*/_jsxs(Hidden, {
          as: itemAs,
          above: hiddenChildProps.above,
          below: hiddenChildProps.below,
          pt: spacing,
          breakpoints: breakpoints,
          children: [withDividers && /*#__PURE__*/_jsx(Divider, {
            mb: spacing
          }), hiddenChildProps.children]
        });
      }
      return /*#__PURE__*/_jsxs(Box, {
        as: itemAs,
        pt: spacing,
        breakpoints: breakpoints,
        children: [withDividers && /*#__PURE__*/_jsx(Divider, {
          mb: spacing
        }), child]
      });
    })
  }));
}
var StandaloneStackContext = /*#__PURE__*/createContext(undefined);
function useStandaloneStackContext() {
  var context = useContext(StandaloneStackContext);
  if (context === undefined) {
    throw Error('StackItem must be used inside of a StackWrapper component');
  }
  return context;
}
var StackWrapper = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var children = _ref2.children,
    _ref2$as = _ref2.as,
    as = _ref2$as === void 0 ? 'default' : _ref2$as,
    spacing = _ref2.spacing,
    withDividers = _ref2.withDividers,
    breakpoints = _ref2.breakpoints,
    otherProps = _objectWithoutProperties(_ref2, _excluded2);
  var _getRoles3 = getRoles(as),
    _getRoles4 = _slicedToArray(_getRoles3, 2),
    wrapperAs = _getRoles4[0],
    itemAs = _getRoles4[1];
  return /*#__PURE__*/_jsx(Wrapper, _objectSpread(_objectSpread({}, otherProps), {}, {
    ref: ref,
    as: wrapperAs,
    compensateSpacing: spacing,
    withDividers: withDividers,
    breakpoints: breakpoints,
    children: /*#__PURE__*/_jsx(StandaloneStackContext.Provider, {
      value: {
        as: itemAs,
        spacing: spacing,
        breakpoints: breakpoints,
        withDividers: withDividers
      },
      children: children
    })
  }));
});
StackWrapper.displayName = 'StackWrapper';
var StackItem = /*#__PURE__*/forwardRef(function (_ref3, ref) {
  var children = _ref3.children,
    hiddenAbove = _ref3.hiddenAbove,
    hiddenBelow = _ref3.hiddenBelow,
    allowUnknownProps = _ref3.allowUnknownProps,
    otherProps = _objectWithoutProperties(_ref3, _excluded3);
  var _useStandaloneStackCo = useStandaloneStackContext(),
    as = _useStandaloneStackCo.as,
    spacing = _useStandaloneStackCo.spacing,
    withDividers = _useStandaloneStackCo.withDividers,
    breakpoints = _useStandaloneStackCo.breakpoints;
  if (hiddenBelow || hiddenAbove) {
    return /*#__PURE__*/_jsxs(Hidden, _objectSpread(_objectSpread({}, allowUnknownProps ? otherProps : undefined), {}, {
      ref: ref,
      as: as,
      pt: spacing,
      below: hiddenBelow,
      above: hiddenAbove,
      breakpoints: breakpoints,
      children: [withDividers && /*#__PURE__*/_jsx(Divider, {
        mb: spacing
      }), children]
    }));
  }
  return /*#__PURE__*/_jsxs(Box, _objectSpread(_objectSpread({}, allowUnknownProps ? otherProps : undefined), {}, {
    ref: ref,
    as: as,
    pt: spacing,
    breakpoints: breakpoints,
    children: [withDividers && /*#__PURE__*/_jsx(Divider, {
      mb: spacing
    }), children]
  }));
});
StackItem.displayName = 'StackItem';
export { StackWrapper, StackItem };
export default Stack;